import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do CPAs and accountants get new clients in Colorado?",
    a: "Local SEO, a tuned Google Business Profile, and dedicated service pages for each practice area — tax prep, bookkeeping, S-corp filings, audit, advisory. CPAs who treat their site as a brochure leave money on the table to firms that built true service-area landing pages.",
  },
  {
    q: "Is SEO worth it for a small accounting firm?",
    a: "Yes, especially in mid-sized Colorado markets. 'CPA near me', 'tax accountant', and 'bookkeeper for small business' have steady year-round volume and convert into long-term retainers. One signed business client typically pays back the SEO investment for the year.",
  },
  {
    q: "What's the highest-ROI marketing channel for accountants?",
    a: "Local SEO + reviews + a content engine that publishes one timely piece per month (tax-law updates, Q4 planning, S-corp guides). Long-term clients hire firms that look like authorities, and authority is built on indexed, dated, useful content.",
  },
  {
    q: "Should accountants run Google Ads?",
    a: "January through April yes — search volume for 'tax preparation' triples and well-built ads capture overflow when your inbound is at capacity. The rest of the year, lean on SEO and referrals rather than paid.",
  },
  {
    q: "Do you handle marketing for niche accounting practices?",
    a: "Yes — cannabis CPAs, real-estate CPAs, construction accounting, nonprofit audits all need niche-specific pages. The narrower the page, the higher the conversion. One signed cannabis-CPA client is worth 20 generic tax-prep leads.",
  },
];

export const Route = createFileRoute("/accountant-marketing-colorado")({
  head: () =>
    industryMarketingHead("Accountant & CPA", "accounting firm", "accountant-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Accountant & CPA"
      industryLower="accounting firm"
      slug="accountant-marketing-colorado"
      subheadline="We help Colorado CPAs and accounting firms attract long-term business clients — not bargain hunters — through dedicated practice-area pages and authority content."
      painPoints={[
        "Your site reads like a brochure and ranks for nothing specific",
        "Tax season is chaos but the other 8 months are quiet because referrals dried up",
        "Niche practices (cannabis, real estate, construction) lose to firms with dedicated landing pages",
      ]}
      serviceDescriptions={{
        seo: "Rank for tax prep, bookkeeping, advisory, S-corp filings, and every niche practice across Colorado markets.",
        googleMaps: "Own 'CPA near me' in the Map Pack so business owners call you first, not the firm down the street.",
        webDesign: "Authority-driven sites that signal trust to long-term retainer clients, not one-time filers.",
        ppc: "Seasonal Google Ads (Jan–Apr) that capture tax-prep overflow without burning Q3/Q4 budget.",
        geoAi: "Get cited when business owners ask ChatGPT 'best CPA in Colorado Springs' or 'S-corp accountant near me'.",
        socialMedia: "LinkedIn + content marketing that positions you as the Colorado expert in your niche.",
      }}
      testimonial={{
        quote:
          "We added three monthly retainer clients in 90 days. The niche cannabis-CPA page closed two of them on its own.",
        role: "CPA Firm Owner",
        city: "Denver",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Accountant & CPA marketing in Colorado Springs", description: "Build long-term business retainers across the Pikes Peak region." },
        { to: "/denver-digital-marketing", label: "Accountant & CPA marketing in Denver", description: "Compete for niche practice clients in Denver's saturated CPA market." },
        { to: "/boulder-digital-marketing", label: "Accountant & CPA marketing in Boulder", description: "Tech, startup, and high-net-worth tax planning in Boulder and the foothills." },
      ]}
    />
  ),
});
